import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  IconButton,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EventIcon from '@mui/icons-material/Event';
import { useIntl } from 'react-intl';

import { useAppDispatch } from '../../../../store/hooks';
import { recordSeriesOutcome, recordEventOutcome, getSeriesById } from '../../../../store/series/thunks';
import { OutcomeChoice, isSeriesOutcome, SeriesOutcomeEnum } from '../../../../store/series/types';
import { AppointmentOutcomeEnum, Appointment } from '../../../../types/Appointment';
import { TimeHelper } from '../../../../utils';
import { OutcomeConfirmStepByChoice } from '../../types';

import styles from './RecordOutcomeModal.module.scss';

type RecordOutcomeModalProps = {
  open: boolean;
  onClose: () => void;
  seriesId: string;
  event: Appointment;
  followUpLabel: string;
  customerName: string;
};

const choiceToValue = (choice: OutcomeChoice): number => {
  switch (choice) {
    case OutcomeChoice.BOUGHT: return SeriesOutcomeEnum.BOUGHT;
    case OutcomeChoice.FAILED: return SeriesOutcomeEnum.FAILED;
    case OutcomeChoice.COMPLETED: return AppointmentOutcomeEnum.COMPLETED;
    case OutcomeChoice.CANCELLED: return AppointmentOutcomeEnum.CANCELLED;
    default: return 0;
  }
};

const selectedClassByChoice: Record<OutcomeChoice, string> = {
  [OutcomeChoice.BOUGHT]: styles.optionSelectedBought,
  [OutcomeChoice.FAILED]: styles.optionSelectedFailed,
  [OutcomeChoice.CANCELLED]: styles.optionSelectedCancelled,
  [OutcomeChoice.COMPLETED]: styles.optionSelectedCompleted,
};

// Choices listed here require an extra "are you sure?" step before dispatch.
// Add an entry to opt a choice into the confirm flow.
const confirmStepByChoice: OutcomeConfirmStepByChoice = {
  [OutcomeChoice.FAILED]: {
    titleId: 'calendar.deleteAppointmentDialogTitle',
    bodyId: 'calendar.deleteAppoinctmentDialog',
  },
};

function RecordOutcomeModalComponent({
  open,
  onClose,
  seriesId,
  event,
  followUpLabel,
  customerName,
}: RecordOutcomeModalProps) {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const [selected, setSelected] = useState<OutcomeChoice | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingConfirm, setPendingConfirm] = useState<OutcomeChoice | null>(null);

  const handleSelect = useCallback((choice: OutcomeChoice) => {
    setSelected(choice);
    setError(null);
  }, []);

  const handleConfirm = useCallback(async () => {
    if (!selected) return;

    if (confirmStepByChoice[selected] && pendingConfirm !== selected) {
      setPendingConfirm(selected);
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const value = choiceToValue(selected);
      if (isSeriesOutcome(selected)) {
        // Pass the triggering event id so the backend marks it COMPLETED and
        // cancels the rest of the active members in one transaction.
        await dispatch(recordSeriesOutcome({ id: seriesId, outcome: value, eventId: event.id })).unwrap();
      } else {
        await dispatch(recordEventOutcome({ eventId: event.id, outcome: value })).unwrap();
      }

      // Refresh series data, deferred so the BE has applied the change first.
      setTimeout(() => {
        dispatch(getSeriesById({ id: seriesId }));
      }, 2000);

      setSelected(null);
      setPendingConfirm(null);
      onClose();
    } catch (e: any) {
      setError(e?.message || intl.formatMessage({ id: 'common.defaultError' }));
    } finally {
      setSubmitting(false);
    }
  }, [selected, pendingConfirm, seriesId, event.id, dispatch, onClose, intl]);

  const handleConfirmBack = useCallback(() => {
    setPendingConfirm(null);
    setSelected(null);
  }, []);

  const handleClose = useCallback(() => {
    if (submitting) return;
    setSelected(null);
    setError(null);
    setPendingConfirm(null);
    onClose();
  }, [submitting, onClose]);

  const confirmStep = pendingConfirm ? confirmStepByChoice[pendingConfirm] : null;

  const timeStart = TimeHelper.getTimeByMinutes(event.time);
  const timeEnd = TimeHelper.getTimeByMinutes(event.time + (event.duration || 0));

  type Option = { choice: OutcomeChoice; label: string; sub: string; testId: string };
  const options: Option[] = [
    {
      choice: OutcomeChoice.BOUGHT,
      label: intl.formatMessage({ id: 'seriesbooking.outcome.bought.label' }),
      sub: intl.formatMessage({ id: 'seriesbooking.outcome.bought.sub' }),
      testId: 'outcome-bought',
    },
    {
      choice: OutcomeChoice.FAILED,
      label: intl.formatMessage({ id: 'seriesbooking.outcome.failed.label' }),
      sub: intl.formatMessage({ id: 'seriesbooking.outcome.failed.sub' }),
      testId: 'outcome-failed',
    },
    {
      choice: OutcomeChoice.CANCELLED,
      label: intl.formatMessage({ id: 'seriesbooking.outcome.cancelled.labelFull' }),
      sub: intl.formatMessage({ id: 'seriesbooking.outcome.cancelled.sub' }),
      testId: 'outcome-cancelled',
    },
    {
      choice: OutcomeChoice.COMPLETED,
      label: intl.formatMessage({ id: 'seriesbooking.outcome.followup.labelFull' }),
      sub: intl.formatMessage({ id: 'seriesbooking.outcome.followup.sub' }),
      testId: 'outcome-followup',
    },
  ];

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth={!confirmStep}
      data-testid="record-outcome-modal"
      PaperProps={confirmStep ? undefined : { className: styles.paper }}
    >
      {confirmStep ? (
        <>
          <DialogTitle>{intl.formatMessage({ id: confirmStep.titleId })}</DialogTitle>
          <DialogContent>{intl.formatMessage({ id: confirmStep.bodyId })}</DialogContent>
          <DialogActions>
            <Button
              data-testid={`record-outcome-${pendingConfirm}-abort`}
              autoFocus
              onClick={handleConfirmBack}
              disabled={submitting}
            >
              {intl.formatMessage({ id: 'common.abort' })}
            </Button>
            <Button
              data-testid={`record-outcome-${pendingConfirm}-confirm`}
              onClick={handleConfirm}
              disabled={submitting}
            >
              {submitting ? <CircularProgress size={18} color="inherit" /> : intl.formatMessage({ id: 'common.confirm' })}
            </Button>
          </DialogActions>
        </>
      ) : (
        <DialogContent className={styles.content}>
          <div className={styles.header}>
            <div>
              <div className={styles.title}>
                {intl.formatMessage({ id: 'seriesbooking.outcome.recordOutcome' })}
              </div>
              <div className={styles.subtitle}>{customerName}</div>
            </div>
            <IconButton onClick={handleClose} disabled={submitting} size="small">
              <CloseIcon fontSize="small" />
            </IconButton>
          </div>

          <div className={styles.eventCard}>
            <EventIcon className={styles.eventIcon} />
            <div className={styles.eventBody}>
              <div className={styles.eventLabel}>{followUpLabel}</div>
              <div className={styles.eventMeta}>
                {event.date}, {timeStart} – {timeEnd}
              </div>
            </div>
          </div>

          {options.map((opt) => (
            <button
              key={opt.choice}
              type="button"
              data-testid={opt.testId}
              className={`${styles.option} ${selected === opt.choice ? selectedClassByChoice[opt.choice] : ''}`}
              onClick={() => handleSelect(opt.choice)}
              disabled={submitting}
            >
              <div className={styles.optionBody}>
                <div className={styles.optionLabel}>{opt.label}</div>
                <div className={styles.optionSub}>{opt.sub}</div>
              </div>
              <span className={styles.radio} />
            </button>
          ))}

          {error && <div className={styles.error}>{error}</div>}

          <div className={styles.actions}>
            <Button
              variant="outlined"
              onClick={handleClose}
              disabled={submitting}
              data-testid="record-outcome-cancel"
              className={styles.actionBtn}
            >
              {intl.formatMessage({ id: 'common.abort' })}
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirm}
              disabled={!selected || submitting}
              data-testid="record-outcome-confirm"
              className={`${styles.actionBtn} ${styles.confirmBtn}`}
            >
              {submitting ? <CircularProgress size={18} color="inherit" /> : intl.formatMessage({ id: 'common.confirm' })}
            </Button>
          </div>
        </DialogContent>
      )}
    </Dialog>
  );
}

export const RecordOutcomeModal = React.memo(RecordOutcomeModalComponent);
